import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  MediaPickerComponent,
  MediaQuestionConfig,
  WorkflowPageConfig,
  WorkflowQuestionConfig,
  WorkflowQuestionType,
  newGUID,
} from '@gripp/shared-logic';
import { useEffect } from 'react';
import { Control, useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../../../themes';
import { DragList } from '../../../dragList/dragList';
import { useFormContext } from '../../../forms/formContext';
import { RoutineAdminInputText } from '../../components/routineAdminInputText';
import { AddQuestion } from './addQuestion';
import { DuplicateQuestion } from './duplicateQuestion';
import {
  IDP_SOURCE_PAGE_INDEX,
  IDP_SOURCE_QUESTION_INDEX,
  buildIdpSourceQuestion,
  isIdpSourceQuestion,
  planIdpSourceInvariant,
} from './idpQuestions';
import { WorkflowAdminQuestion } from './workflowAdminQuestion';

type WorkflowAdminPageProps = {
  page: WorkflowPageConfig;
  index: number;
  control: Control;
  mediaPicker?: MediaPickerComponent;
};

export const WorkflowAdminPage = (props: WorkflowAdminPageProps) => {
  const { t } = useTranslation();
  const { index, control } = props;
  const { getValues, setValue } = useFormContext();
  const indexPrefix = `pages.${index}`;
  const i18nPrefix = 'routines.workflow.admin.page';
  const questionI18nPrefix = 'routines.workflow.admin.question';
  const {
    fields: questionFields,
    append: appendQuestion,
    remove: removeQuestion,
    move: moveQuestion,
    update: updateQuestion,
    insert: insertQuestion,
  } = useFieldArray({
    control: control,
    name: `${indexPrefix}.questions`,
  });
  const watchedQuestions = useWatch({
    control,
    name: `${indexPrefix}.questions`,
  });
  const idpEnabled = !!useWatch({ control, name: 'idpEnabled' });

  /**
   * The pinned document question extraction reads from. It is rendered outside
   * the `DragList` — that is what locks its position, since `DragList` owns the
   * drag interaction and its own copy of the order. `questionFields[0]` carries
   * the inserted config values (only its `id` is react-hook-form's key rather
   * than the question's own id).
   */
  const firstQuestion = questionFields[0] as unknown as
    | WorkflowQuestionConfig
    | undefined;
  const pinnedQuestion =
    idpEnabled &&
    index === IDP_SOURCE_PAGE_INDEX &&
    isIdpSourceQuestion(firstQuestion)
      ? firstQuestion
      : undefined;
  const hasPinnedIdpSource = !!pinnedQuestion;
  const draggableQuestions = hasPinnedIdpSource
    ? questionFields.slice(1)
    : questionFields;
  const draggableOffset = hasPinnedIdpSource ? 1 : 0;

  /**
   * Restore the single-source invariant whenever IDP is on. Owned by the first
   * page so two page instances cannot both insert a source question. Toggling IDP
   * off stops enforcing but leaves the question alone (PRD: it "can't be deleted
   * or reordered *while IDP is enabled*"), so re-enabling re-pins the same
   * question instead of adding a second one.
   *
   * The dependency list is deliberately just the toggle and the page: the effect
   * reads current form values itself, so re-running it on every render would only
   * repeat work it has already done.
   */
  useEffect(() => {
    if (!idpEnabled || index !== IDP_SOURCE_PAGE_INDEX) return;

    const pages: WorkflowPageConfig[] = getValues('pages') ?? [];
    const plan = planIdpSourceInvariant(pages);

    plan.demote.forEach((position) => {
      setValue(
        `pages.${position.pageIndex}.questions.${position.questionIndex}.isIdpSource`,
        false,
        { shouldDirty: true }
      );
    });

    if (plan.moveToPinnedFrom !== undefined) {
      moveQuestion(plan.moveToPinnedFrom, IDP_SOURCE_QUESTION_INDEX);
    }

    if (plan.insert) {
      insertQuestion(
        IDP_SOURCE_QUESTION_INDEX,
        buildIdpSourceQuestion(t(`${questionI18nPrefix}.idpSourceInstructions`))
      );
    }
  }, [idpEnabled, index]);

  const DefaultQuestion: WorkflowQuestionConfig = {
    id: newGUID(),
    name: `Question ${questionFields.length + 1}`,
    order: questionFields.length + 1,
    type: WorkflowQuestionType.Text,
    required: false,
    instructions: '',
  };

  const onAddQuestion = (type: WorkflowQuestionType, index: number) => {
    const newQuestion = {
      ...DefaultQuestion,
      order: index + 1,
      name: `Question ${index + 1}`,
    };
    insertQuestion(index + 1, {
      ...newQuestion,
      type,
    });
  };

  const onAppendQuestion = (type: WorkflowQuestionType) => {
    appendQuestion({
      ...DefaultQuestion,
      type,
    });
  };

  /** True for the pinned source question while IDP is on: it cannot be removed. */
  const isLockedQuestion = (questionIndex: number) =>
    hasPinnedIdpSource && questionIndex === IDP_SOURCE_QUESTION_INDEX;

  const onDuplicateQuestion = (questionIndex: number) => {
    if (isLockedQuestion(questionIndex)) return;
    const original = watchedQuestions?.[questionIndex];
    if (!original) return;
    const originalQuestion = original as WorkflowQuestionConfig;
    const duplicatedQuestion: WorkflowQuestionConfig &
      Pick<MediaQuestionConfig, 'isIdpSource'> = {
      ...originalQuestion,
      id: newGUID(),
      order: questionIndex + 2,
      name: `Question ${questionIndex + 2}`,
      isHeadline: false,
      // Single-instance flags never survive a copy — the same rule as isHeadline,
      // and the one that keeps a second IDP source from ever being created.
      isIdpSource: false,
    };

    insertQuestion(questionIndex + 1, duplicatedQuestion);
  };

  const onDeleteQuestion = (questionIndex: number) => {
    if (isLockedQuestion(questionIndex)) return;
    removeQuestion(questionIndex);
  };

  const onReorderedFromTo = (fromIndex: number, toIndex: number) => {
    moveQuestion(fromIndex + draggableOffset, toIndex + draggableOffset);
  };

  const renderQuestion = (
    question: WorkflowQuestionConfig,
    locked: boolean
  ) => {
    const indexInFields = questionFields.findIndex((q) => q.id === question.id);
    return (
      <View style={styles.questionContainer}>
        {!locked && (
          <View style={styles.questionActions}>
            <Pressable
              style={styles.delete}
              onPress={() => onDeleteQuestion(indexInFields)}
              accessibilityRole="button"
              accessibilityLabel={t(`${i18nPrefix}.deleteQuestion`)}
            >
              <MaterialCommunityIcons
                name="close-circle-outline"
                color={Colors.primary}
                size={24}
              />
            </Pressable>
          </View>
        )}
        <WorkflowAdminQuestion
          key={`question_${question.id}`}
          control={control}
          index={indexInFields}
          question={question}
          type={question.type}
          pagePrefix={indexPrefix}
          mediaPicker={props.mediaPicker}
        />
        {!locked && (
          <DuplicateQuestion
            i18nPrefix={i18nPrefix}
            onPress={() => onDuplicateQuestion(indexInFields)}
          />
        )}
        {/* Kept on the locked question too: when it is the only question on the
            page, this is the sole way to add the next one. */}
        <AddQuestion
          i18nPrefix={i18nPrefix}
          onPress={(type) => onAddQuestion(type, indexInFields)}
        />
      </View>
    );
  };

  const renderItem = (question: unknown) =>
    renderQuestion(question as WorkflowQuestionConfig, false);

  return (
    <View style={styles.container}>
      <Text style={styles.header}>
        {t(`${i18nPrefix}.page`, { page: index + 1 })}
      </Text>

      <RoutineAdminInputText
        control={props.control}
        i18nPrefix={i18nPrefix}
        name="instructions"
        indexPrefix={indexPrefix}
        textArea={true}
      />
      <View style={styles.questions}>
        {pinnedQuestion && (
          <View style={styles.pinnedQuestion}>
            <Text style={styles.pinnedLabel}>
              {t(`${questionI18nPrefix}.idpSource`)}
            </Text>
            {renderQuestion(pinnedQuestion, true)}
          </View>
        )}
        <DragList
          list={draggableQuestions}
          pivotKey={'id'}
          sortKey={'order'}
          renderCustomItem={renderItem}
          onReorderedFromTo={onReorderedFromTo}
          customItemStyle={styles.question}
        />
        {questionFields.length === 0 && (
          <AddQuestion i18nPrefix={i18nPrefix} onPress={onAppendQuestion} />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderColor: Colors.black,
    paddingHorizontal: 10,
  },
  header: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 20,
  },
  questions: {
    marginBottom: 20,
    borderColor: Colors.grayDivider,
    borderWidth: 1,
    paddingTop: 10,
  },
  question: {
    height: '100%',
  },
  pinnedQuestion: {
    borderBottomWidth: 1,
    borderColor: Colors.graySpacer,
    paddingBottom: 10,
  },
  pinnedLabel: {
    fontWeight: '700',
    paddingHorizontal: 12,
    color: Colors.grayText,
  },
  questionContainer: {
    flex: 1,
    maxWidth: '80%',
    padding: 12,
  },
  questionActions: {
    alignSelf: 'flex-end',
    flexDirection: 'row',
    gap: 4,
  },
  delete: {
    justifyContent: 'center',
  },
});
